/**
 * `<ask_user id="…" status="…">{json}</ask_user>` question cards, as the API
 * writes them (see the API's `formatAskUserTag`). The JSON body has `<`, `>`
 * and `&` escaped so it can never close the tag early.
 */

export interface AskUserOption {
  label: string;
}

export interface AskUserQuestion {
  question: string;
  options: AskUserOption[];
  multiSelect: boolean;
}

/** One entry per question. Empty `selected` and no `other` means skipped. */
export interface AskUserAnswer {
  selected: string[];
  other?: string;
}

export type AskUserStatus = 'pending' | 'answered' | 'skipped';

export interface AskUserCardData {
  /** The tool call id, sanitized to `[A-Za-z0-9_-]`; sent back when answering. */
  id: string;
  status: AskUserStatus;
  questions: AskUserQuestion[];
  answers?: AskUserAnswer[];
}

export const ASK_USER_OPEN_TAG = '<ask_user';
export const ASK_USER_CLOSE_TAG = '</ask_user>';

const attribute = (openingTag: string, name: string): string | undefined =>
  new RegExp(`\\b${name}="([^"]*)"`).exec(openingTag)?.[1];

/**
 * Parses one card from its opening tag and body. Returns null for a body that
 * is still streaming in or malformed.
 */
export const parseAskUserCard = (openingTag: string, body: string): AskUserCardData | null => {
  const id = attribute(openingTag, 'id');
  if (!id) return null;
  try {
    const parsed = JSON.parse(body.trim());
    if (!Array.isArray(parsed?.questions) || parsed.questions.length === 0) return null;
    const rawStatus = attribute(openingTag, 'status');
    const status: AskUserStatus = rawStatus === 'answered' || rawStatus === 'skipped' ? rawStatus : 'pending';
    return {
      id,
      status,
      questions: parsed.questions,
      answers: Array.isArray(parsed.answers) ? parsed.answers : undefined,
    };
  } catch {
    return null;
  }
};

const encodeTagJson = (value: unknown): string =>
  JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Rewrites a card in `content` as answered, mirroring what the server saves,
 * so it collapses before the synced copy arrives.
 */
export const markAskUserAnswered = (
  content: string,
  card: AskUserCardData,
  answers: AskUserAnswer[]
): string => {
  const pattern = new RegExp(`<ask_user id="${escapeRegExp(card.id)}"[^>]*>[\\s\\S]*?</ask_user>`);
  const body = encodeTagJson({ questions: card.questions, answers });
  const replacement = `<ask_user id="${card.id}" status="answered">${body}</ask_user>`;
  return content.replace(pattern, () => replacement);
};

/** Finds a card by id in saved message content. */
export const findAskUserCard = (content: string, id: string): AskUserCardData | null => {
  const pattern = new RegExp(`(<ask_user id="${escapeRegExp(id)}"[^>]*)>([\\s\\S]*?)</ask_user>`);
  const match = pattern.exec(content);
  return match ? parseAskUserCard(match[1], match[2]) : null;
};

export const isAnswerEmpty = (answer: AskUserAnswer | undefined): boolean =>
  !answer || (answer.selected.length === 0 && !(answer.other ?? '').trim());

/** The last still-pending card in saved message content, if any. */
export const findPendingAskUserCard = (content: string): AskUserCardData | null => {
  const pattern = /(<ask_user\b[^>]*status="pending"[^>]*)>([\s\S]*?)<\/ask_user>/g;
  let found: AskUserCardData | null = null;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(content)) !== null) {
    found = parseAskUserCard(match[1], match[2]) ?? found;
  }
  return found;
};
